import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // Assumindo que o caminho está correto
import TopBar from '../components/TopBar';
import './PoloSelection.css'; // Crie um CSS similar ao CensusChoice se necessário

interface ActingLocation {
  state: string;
  city: string;
  polo: string;
  address: string;
  id_acting: number;
}

const PoloSelection: React.FC = () => {
  const { token, id_user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<ActingLocation[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [polos, setPolos] = useState<ActingLocation[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedPolo, setSelectedPolo] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [showToken, setShowToken] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string>('');

  useEffect(() => {
    if (!token || !id_user) {
      alert('Sessão expirada. Faça login novamente.');
      navigate('/');
      return;
    }

    const fetchLocations = async () => {
      try {
        const response = await fetch('https://censoprev.com/backend/acting', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Erro ao carregar os dados dos polos.');
        }

        const data: ActingLocation[] = await response.json();
        setLocations(data);

        const stateArray = data.map((item) => item.state);
        const uniqueStates = stateArray.filter((value, index, self) => self.indexOf(value) === index).sort();
        setStates(uniqueStates);
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar os dados. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [token, id_user, navigate]);

  useEffect(() => {
    if (selectedState) {
      const cityArray = locations.filter((item) => item.state === selectedState).map((item) => item.city);
      const filteredCities = cityArray.filter((value, index, self) => self.indexOf(value) === index).sort();
      setCities(filteredCities);
      setSelectedCity('');
      setSelectedPolo(null);
      setPolos([]);
    }
  }, [selectedState, locations]);

  useEffect(() => {
    if (selectedCity) {
      const filteredPolos = locations.filter(
        (item) => item.state === selectedState && item.city === selectedCity
      );
      setPolos(filteredPolos);
      setSelectedPolo(null);
    }
  }, [selectedCity, selectedState, locations]);

  const handleProceed = () => {
    if (!selectedPolo) {
      alert('Selecione um polo para continuar.');
      return;
    }
    setShowSchedule(true);
  };

  const generateCalendar = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayWeek = firstDayOfMonth.getDay();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    // Previous month days
    for (let i = firstDayWeek - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, type: 'prev' });
    }

    // Current month days
    for (let d = 1; d <= lastDayOfMonth.getDate(); d++) {
      days.push({ day: d, type: 'current' });
    }

    // Next month days
    let nextDay = 1;
    while (days.length < 42) { // Max 6 weeks
      days.push({ day: nextDay++, type: 'next' });
    }

    return days;
  };

  const calendarDays = generateCalendar();
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const handleDayClick = (day: number, type: string) => {
    if (type === 'current') {
      setSelectedDay(day);
    }
  };

  const handleConfirm = async () => {
    if (selectedDay === null || !selectedTime) {
      alert('Selecione o dia e o horário.');
      return;
    }

    const now = new Date();
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const randomDigits = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
    const dateNoHyphen = dateStr.replace(/-/g, '');
    const timeNoColon = selectedTime.replace(/:/g, '');
    const genToken = randomDigits + dateNoHyphen + timeNoColon;

    try {
      const scheduleResponse = await fetch('https://censoprev.com/backend/censo_in_person', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          consultation_date: dateStr,
          time: selectedTime,
          token: genToken,
        }),
      });

      if (!scheduleResponse.ok) {
        throw new Error('Erro ao agendar.');
      }

      setGeneratedToken(genToken);
      setShowSchedule(false);
      setShowToken(true);
    } catch (error) {
      console.error(error);
      alert('Erro ao agendar.');
    }
  };

  const handleClose = () => {
    setShowSchedule(false);
    setSelectedDay(null);
    setSelectedTime('');
  };

  if (loading) {
    return <div>Carregando polos...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <>
      <TopBar />
      <div className="selection-wrapper">
        <div className="selection-content">
          <h2>Selecione o Polo para Censo Presencial</h2>
          
          <div className="form-group">
            <label>Estado:</label>
            <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
              <option value="">Selecione o estado</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          {selectedState && (
            <div className="form-group">
              <label>Cidade:</label>
              <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
                <option value="">Selecione a cidade</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedCity && (
            <div className="form-group">
              <label>Polo:</label>
              <select
                value={selectedPolo ?? ''}
                onChange={(e) => setSelectedPolo(Number(e.target.value))}
              >
                <option value="">Selecione o polo</option>
                {polos.map((polo) => (
                  <option key={polo.id_acting} value={polo.id_acting}>
                    {polo.polo} - {polo.address}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button onClick={handleProceed} disabled={!selectedPolo}>
            Continuar
          </button>
        </div>
      </div>

      {showSchedule && (
        <div className="schedule-overlay">
          <div className="schedule-content">
            <h3>{months[currentMonth]} {currentYear}</h3>
            <div className="calendar-grid">
              <div className="weekday">Do</div>
              <div className="weekday">Se</div>
              <div className="weekday">Te</div>
              <div className="weekday">Qu</div>
              <div className="weekday">Qu</div>
              <div className="weekday">Se</div>
              <div className="weekday">Sa</div>
              {calendarDays.map((d, index) => (
                <div
                  key={index}
                  className={`day ${d.type} ${d.type === 'current' && d.day === selectedDay ? 'selected' : ''}`}
                  onClick={() => handleDayClick(d.day, d.type)}
                >
                  {d.day}
                </div>
              ))}
            </div>
            <div className="time-selection">
              <label>Selecione o horário que melhor lhe atende</label>
              <select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}>
                <option value="">Selecione o horário</option>
                <option value="08:00">08:00</option>
                <option value="09:00">09:00</option>
                <option value="10:00">10:00</option>
                <option value="11:00">11:00</option>
                <option value="12:00">12:00</option>
                <option value="13:00">13:00</option>
                <option value="14:00">14:00</option>
                <option value="15:00">15:00</option>
                <option value="16:00">16:00</option>
                <option value="17:00">17:00</option>
                {/* Adicione mais horários conforme necessário */}
              </select>
            </div>
            <div className="buttons">
              <button onClick={handleConfirm}>CONFIRMAR</button>
              <button onClick={handleClose}>FECHAR</button>
            </div>
          </div>
        </div>
      )}

      {showToken && (
        <div className="token-overlay">
          <div className="token-content">
            <h2>Agendamento Concluído</h2>
            <p>Seu token para o censo presencial é:</p>
            <strong>{generatedToken}</strong>
            <button onClick={() => setShowToken(false)}>Fechar</button>
          </div>
        </div>
      )}
    </>
  );
};

export default PoloSelection;